/**
 * The `t()` lookup, as a pure function so the fallback chain is unit-testable
 * without rendering LanguageProvider.
 *
 * Chain (COL-478): active language → English → German → caller fallback →
 * key (dev) / "" (prod).
 *
 * Two deliberate asymmetries carried over from the pre-COL-478 behaviour:
 *   - English never falls back to German. Callers pass English fallback
 *     strings, and showing German labels after switching to English looked
 *     like a bug.
 *   - German falls back to English (rollout gaps), but not further.
 * The four new languages fall back to English first because it is the most
 * complete column, then to German because it is the app default.
 *
 * A stored value identical to its key is a placeholder row, not a
 * translation — treated as a miss so the caller's fallback wins instead of a
 * dotted key like "contact.attach" leaking into the UI.
 */
import type { SupportedLanguage } from "./languages";

export type TranslationDict = Record<string, string>;

export type TranslationSources = {
  /** Dict for the active language. */
  active: TranslationDict | undefined;
  /** English dict, when known. Consulted for every language except English. */
  en?: TranslationDict;
  /** German dict, when known. Consulted only for the four non-base languages. */
  de?: TranslationDict;
};

function hit(dict: TranslationDict | undefined, key: string): string | null {
  const value = dict?.[key];
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (trimmed.length === 0 || trimmed === key) return null;
  return value;
}

export type ResolvedTranslation = {
  value: string;
  /** Which tier answered; "none" means the key is missing everywhere. */
  source: "active" | "en" | "de" | "fallback" | "none";
};

export function resolveTranslation(
  key: string,
  language: SupportedLanguage,
  sources: TranslationSources,
  fallback: string | undefined,
  options: { dev: boolean; fallbackBeatsOtherLanguages?: boolean }
): ResolvedTranslation {
  if (!key) return { value: "", source: "none" };

  const active = hit(sources.active, key);
  if (active !== null) return { value: active, source: "active" };

  // Copy that ships with the code (COL-479 dictionaries) is already written
  // in the reader's own language, so it beats a row from a DIFFERENT one.
  // Without this a German tenant whose `de` column is empty would render the
  // English row instead of the German literal sitting right there — worse
  // than the behaviour the sweep replaced. For an ordinary t() call the
  // fallback is an English developer string, so it stays below the tiers.
  if (options.fallbackBeatsOtherLanguages) {
    if (typeof fallback === "string" && fallback.length > 0) {
      return { value: fallback, source: "fallback" };
    }
  }

  if (language !== "en") {
    const en = hit(sources.en, key);
    if (en !== null) return { value: en, source: "en" };
  }

  if (language !== "en" && language !== "de") {
    const de = hit(sources.de, key);
    if (de !== null) return { value: de, source: "de" };
  }

  if (typeof fallback === "string" && fallback.length > 0) {
    return { value: fallback, source: "fallback" };
  }

  return { value: options.dev ? key : "", source: "none" };
}
